import {useCallback, useEffect, useRef} from 'react';
import {api, trpc} from '@common-web/lib/trpc';
import {Spinner} from '@common-web/components/ui/spinner';

// The utilization-report iframe and its token handshake, shared by the
// all-groups report page and the Utilization tab on a group's page. One copy
// on purpose: this is the code that decides which origin we trust and what we
// hand it, and two copies drifting apart is how a check like that quietly
// stops holding.
//
// The iframe src is the bare report URL. The token travels only by
// postMessage, never in a URL. Two invariants that are easy to "optimise"
// away. Do not:
//
// 1. Let the iframe renew against the report service. Every refresh must come
//    back through this parent and `getToken`, which re-checks THIS session, the
//    tenant flag, and the permission. That is the only thing stopping a stolen
//    token from renewing itself. Keep `getToken` on the vanilla `api` client
//    (not a cached `useQuery`), so a refresh is always a real server mint.
// 2. Use `'*'` as a postMessage targetOrigin, in either direction. Outbound we
//    pass the configured report origin, so the token cannot land in whatever
//    document happens to be in the frame (a redirect, a navigation). Inbound we
//    require both `event.source === iframe.contentWindow` and
//    `event.origin === reportOrigin` before doing anything. `'*'` here is a
//    credential leak.
//
// `groupId` pins the frame to ONE group and does two distinct things, both
// needed:
//   1. `?group=<id>` on the URL tells the report which group to render. That
//      is display only; a viewer can edit it.
//   2. the minted token carries `groups: [id]`, so the report service
//      hard-restricts to that group and answers NOT_FOUND for any other, which
//      is what makes (1) safe. Without the claim the token is tenant-wide and
//      editing the URL would return another group's data.
// Omit `groupId` for the tenant-wide report.
const TOKEN_REQUEST = 'util:token-request';
const TOKEN = 'util:token';
const TOKEN_ERROR = 'util:token-error';
const SESSION_ENDED = 'Your session has ended.';

interface Props {
  /** Pin the report to a single VBA group (see above). Omit for the all-groups report. */
  groupId?: string;
  /** iframe title, read by screen readers, so say which report this is. */
  title: string;
  /** Wrapper classes. Callers set their own height: a full page and a tab pane differ. */
  className?: string;
}

export function UtilizationReportFrame({groupId, title, className}: Props) {
  const {data: reportUrl, error, isLoading} = trpc.clients.utilizationReport.getUrl.useQuery();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const reportOrigin = reportUrl ? httpOrigin(reportUrl) : null;

  const onMessage = useCallback(
    async (event: MessageEvent) => {
      if (!reportOrigin) return;
      const frame = iframeRef.current?.contentWindow;
      if (!frame || event.source !== frame || event.origin !== reportOrigin) return;
      if (!isTokenRequest(event.data)) return;

      try {
        const token = await api.clients.utilizationReport.getToken.query(
          groupId ? {groupId} : undefined,
        );
        frame.postMessage({type: TOKEN, token}, reportOrigin);
      } catch {
        frame.postMessage({type: TOKEN_ERROR, reason: SESSION_ENDED}, reportOrigin);
      }
    },
    [reportOrigin, groupId],
  );

  useEffect(() => {
    if (!reportOrigin) return;
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [reportOrigin, onMessage]);

  if (isLoading) {
    return (
      <div className='flex flex-1 items-center justify-center py-16'>
        <Spinner className='h-8 w-8' />
      </div>
    );
  }

  if (error || !reportUrl || !reportOrigin) {
    return (
      <div className='mx-auto max-w-lg p-6'>
        <p className='text-sm text-red-600'>
          Unable to load the utilization report. Please try again.
        </p>
      </div>
    );
  }

  // The report reads `group` from its own query string. Encoded because it is
  // interpolated into a URL; the value is a VBA group id, but encode regardless
  // rather than trust the shape.
  const src = groupId
    ? `${reportUrl}${reportUrl.includes('?') ? '&' : '?'}group=${encodeURIComponent(groupId)}`
    : reportUrl;

  return (
    <div className={className ?? 'flex min-h-[calc(100vh-5rem)] flex-1 flex-col'}>
      <iframe
        ref={iframeRef}
        className='h-full min-h-full w-full flex-1 border-0'
        src={src}
        title={title}
        referrerPolicy='no-referrer'
      />
    </div>
  );
}

function isTokenRequest(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const message = data as {type?: unknown; v?: unknown};
  return message.type === TOKEN_REQUEST && message.v === 1;
}

function httpOrigin(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    return parsed.origin;
  } catch {
    return null;
  }
}
